import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../App';
import AdminLevelFilter from './AdminLevelFilter';
import { buildAttachmentUrl } from '../utils/attachments';
import { getStatusLabel } from '../utils/status';
import { complaintsApi } from '../services/formsApi';

const camposRelatoLabels = {
  identificacao: 'Você gostaria de se identificar?',
  local_ocorrencia: 'A denúncia se refere a algo ocorrido em...',
  tipo_denuncia: 'O que você está denunciando?',
  quando_ocorreu: 'Quando ocorreu?',
  departamento: 'Em que departamento ocorreu o fato?',
  relato_detalhado: 'Relate o ocorrido da forma mais detalhada possível',
  tentou_esconder: 'Alguém tentou esconder o fato?',
  presenciou_fatos: 'Você presenciou os fatos?',
  outras_testemunhas: 'Outras pessoas têm conhecimento ou presenciaram os fatos relatados?'
};

const camposAdminLabels = {
  status_conclusao: 'Status de Conclusão',
  data_conclusao: 'Data de Conclusão',
  resposta_inicial_denunciante: 'Resposta Inicial ao Denunciante',
  plano_investigacao: 'Plano de Investigação Recomendado',
  fatos_apurados: 'Fatos Apurados na Investigação',
  conclusao: 'Conclusão',
  resposta_denunciante: 'Resposta ao Denunciante',
  plano_acao: 'Plano de Ação Realizado'
};

const ClientAdminDashboard = ({ clientSlug, clientName }) => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [showDetail, setShowDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [modalActiveTab, setModalActiveTab] = useState('denuncia');
  
  // Estados para filtro por níveis
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  // Campos do formulário do cliente: dão o texto de cada nível de acesso no filtro.
  const [formFields, setFormFields] = useState([]);
  
  // Verificar se é investigador externo
  const isInvestigador = user?.is_investigador_externo;

  useEffect(() => {
    loadDashboardData();
    loadComplaints();
  }, [clientSlug, selectedPeriod]);

  useEffect(() => {
    if (!clientSlug) return;
    complaintsApi.getPublicForm(clientSlug)
      .then(form => setFormFields(form?.fields || []))
      .catch(() => setFormFields([])); // sem formulário, o filtro cai no mapa fixo
  }, [clientSlug]);

  useEffect(() => {
    if (showDetail && detailData) {
      // Logs de debug para análise
      console.log('🟦 detailData:', detailData);
      console.log('🟦 detailData.form_fields:', detailData.form_fields);
      console.log('🟦 detailData.responses:', detailData.responses);
    }
  }, [showDetail, detailData]);

  const loadDashboardData = async () => {
    try {
      const response = await axios.get(`/api/complaints/client/${clientSlug}/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dashboard');
    }
  };

  const loadComplaints = async (level = 'all') => {
    try {
      const url = level === 'all' 
        ? `/api/complaints/client/${clientSlug}/list`
        : `/api/complaints/client/${clientSlug}/list?level=${level}`;
      
      const response = await axios.get(url);
      setComplaints(response.data.complaints);
      setFilteredComplaints(response.data.complaints);
    } catch (error) {
      toast.error('Erro ao carregar denúncias');
    } finally {
      setLoading(false);
    }
  };

  const updateComplaintStatus = async (complaintId, newStatus) => {
    try {
      await axios.put(`/api/complaints/client/${clientSlug}/${complaintId}/status`, {
        status: newStatus
      });
      toast.success('Status atualizado com sucesso!');
      loadComplaints(selectedLevel);
      loadDashboardData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  // Função para lidar com mudança de filtro
  const handleFilterChange = (level) => {
    setSelectedLevel(level);
    loadComplaints(level);
  };

  const getStatusColor = (status) => {
    const colors = {
      'received': 'bg-yellow-100 text-yellow-800',
      'investigating': 'bg-blue-100 text-blue-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // getStatusLabel is imported from ../utils/status (single canonical map).

  const formatDate = (dateString) => {
    if (!dateString) return 'Data não disponível';
    const d = new Date(dateString);
    return isNaN(d) ? 'Data inválida' : d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressPercentage = (current, total) => {
    if (total === 0) return 0;
    return Math.round((current / total) * 100);
  };

  const handleShowDetail = async (complaintId) => {
    setDetailLoading(true);
    setDetailError('');
    setShowDetail(true);
    try {
      const response = await axios.get(`/api/complaints/client/${clientSlug}/${complaintId}`);
      setDetailData(response.data);
    } catch (err) {
      setDetailError('Erro ao carregar detalhes da denúncia.');
      setDetailData(null);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (process.env.NODE_ENV !== 'production') {
    // Adicione aqui todos os console.log de debug encontrados nas linhas listadas
    // Exemplo:
    // if (process.env.NODE_ENV !== "production") {
    console.log('�� Buscando denúncias do cliente...');
    // if (process.env.NODE_ENV !== "production") {
    console.log('📊 Dados recebidos:', complaints);
  }

  return (
    <div className="rf-screen">
      {/* Header */}
      <div className="rf-hero-band">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Dashboard do Cliente</h1>
            <p className="text-blue-100 mt-1">{clientName} - {clientSlug}</p>
            <p className="text-sm text-blue-200 mt-2">Painel de Controle e Analytics</p>
          </div>
          <div className="flex items-center space-x-4">
            <a
              href="/analytics-disruptive"
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              🚀 Dashboard Interativo
            </a>
            <div className="text-right">
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <p className="text-sm text-blue-100">Período de Análise</p>
                <select 
                  value={selectedPeriod} 
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="mt-1 bg-white text-gray-900 rounded px-3 py-1 text-sm"
                >
                  <option value="7">Últimos 7 dias</option>
                  <option value="30">Últimos 30 dias</option>
                  <option value="90">Últimos 90 dias</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="rf-surface mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Visão Geral
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📈 Analytics
            </button>
            <button
              onClick={() => setActiveTab('complaints')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'complaints'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📝 Denúncias
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'overview' && dashboardData && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="rf-surface p-6 border-l-4 border-blue-500">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total de Denúncias</p>
                  <p className="text-2xl font-semibold text-gray-900">{dashboardData.stats.total_complaints}</p>
                  <p className="text-xs text-gray-500">+{dashboardData.stats.last_30_days} este mês</p>
                </div>
              </div>
            </div>

            <div className="rf-surface p-6 border-l-4 border-yellow-500">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Pendentes</p>
                  <p className="text-2xl font-semibold text-gray-900">{dashboardData.stats.pending_complaints}</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-yellow-500 h-2 rounded-full" 
                      style={{width: `${getProgressPercentage(dashboardData.stats.pending_complaints, dashboardData.stats.total_complaints)}%`}}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rf-surface p-6 border-l-4 border-blue-500">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Em Investigação</p>
                  <p className="text-2xl font-semibold text-gray-900">{dashboardData.stats.in_progress_complaints}</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{width: `${getProgressPercentage(dashboardData.stats.in_progress_complaints, dashboardData.stats.total_complaints)}%`}}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rf-surface p-6 border-l-4 border-green-500">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-100 text-green-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Resolvidas</p>
                  <p className="text-2xl font-semibold text-gray-900">{dashboardData.stats.resolved_complaints}</p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full" 
                      style={{width: `${getProgressPercentage(dashboardData.stats.resolved_complaints, dashboardData.stats.total_complaints)}%`}}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rf-surface p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Denúncias por Tipo</h3>
              <div className="space-y-3">
                {dashboardData.complaints_by_type && dashboardData.complaints_by_type.length > 0 ? (
                  dashboardData.complaints_by_type.map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{item.tipo_denuncia || 'Não especificado'}</span>
                      <div className="flex items-center">
                        <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                          <div 
                            className="bg-blue-500 h-2 rounded-full" 
                            style={{width: `${getProgressPercentage(item.count, dashboardData.stats.total_complaints)}%`}}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{item.count}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">Nenhum dado disponível</p>
                )}
              </div>
            </div>

            <div className="rf-surface p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Denúncias por Departamento</h3>
              <div className="space-y-3">
                {dashboardData.complaints_by_department && dashboardData.complaints_by_department.length > 0 ? (
                  dashboardData.complaints_by_department.map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{item.departamento || 'Não especificado'}</span>
                      <div className="flex items-center">
                        <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{width: `${getProgressPercentage(item.count, dashboardData.stats.total_complaints)}%`}}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{item.count}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">Nenhum dado disponível</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Complaints */}
          <div className="rf-surface p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Denúncias Recentes</h3>
              <button 
                onClick={() => setActiveTab('complaints')}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Ver todas →
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full max-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Token
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      Status
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                      Data
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dashboardData.recent_complaints && dashboardData.recent_complaints.length > 0 ? (
                    dashboardData.recent_complaints.map((complaint) => (
                      <tr key={complaint.id} className="hover:bg-gray-50">
                        <td className="px-3 py-4 text-sm font-medium text-gray-900 break-all">
                          <span className="inline-block bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                            {complaint.tracking_token.substring(0, 8)}...
                          </span>
                        </td>
                        <td className="px-3 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(complaint.status)}`}>
                            {getStatusLabel(complaint.status)}
                          </span>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 break-words">
                          {formatDate(complaint.submitted_at)}
                        </td>
                        <td className="px-3 py-4 text-sm font-medium">
                          <button
                            onClick={() => setActiveTab('complaints')}
                            className="text-blue-600 hover:text-blue-900 whitespace-nowrap"
                          >
                            Ver Detalhes
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                        Nenhuma denúncia recente
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'analytics' && dashboardData && (
        <div className="space-y-6">
          {/* Trend Chart */}
          <div className="rf-surface p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Tendência Temporal (Últimos 6 meses)</h3>
            <div className="h-64 flex items-end justify-center space-x-2">
              {dashboardData.trend_data && dashboardData.trend_data.length > 0 ? (
                dashboardData.trend_data.map((item, index) => {
                  const maxValue = Math.max(...dashboardData.trend_data.map(d => d.count));
                  const height = maxValue > 0 ? (item.count / maxValue) * 200 : 0;
                  return (
                    <div key={index} className="flex flex-col items-center">
                      <div 
                        className="bg-blue-500 rounded-t w-8 transition-all duration-300 hover:bg-blue-600"
                        style={{height: `${height}px`}}
                        title={`${item.month}: ${item.count} denúncias`}
                      ></div>
                      <span className="text-xs text-gray-500 mt-2 rotate-45 origin-left">
                        {new Date(item.month + '-01').toLocaleDateString('pt-BR', {month: 'short'})}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Nenhum dado disponível para o período
                </div>
              )}
            </div>
          </div>

          {/* Status Distribution */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rf-surface p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuição por Status</h3>
              <div className="space-y-4">
                {Object.entries({
                  'received': dashboardData.stats.pending_complaints,
                  'investigating': dashboardData.stats.in_progress_complaints,
                  'resolved': dashboardData.stats.resolved_complaints,
                  'closed': dashboardData.stats.closed_complaints
                }).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-3 ${getStatusColor(status).replace('bg-', 'bg-').replace('text-', '')}`}></div>
                      <span className="text-sm text-gray-600">{getStatusLabel(status)}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className={`h-2 rounded-full ${getStatusColor(status).replace('bg-', 'bg-').replace('text-', '')}`}
                          style={{width: `${getProgressPercentage(count, dashboardData.stats.total_complaints)}%`}}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rf-surface p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuição por Público</h3>
              <div className="space-y-4">
                {dashboardData.complaints_by_public && dashboardData.complaints_by_public.length > 0 ? (
                  dashboardData.complaints_by_public.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${
                          index === 0 ? 'bg-blue-500' : 
                          index === 1 ? 'bg-green-500' : 
                          index === 2 ? 'bg-yellow-500' : 'bg-gray-500'
                        }`}></div>
                        <span className="text-sm text-gray-600">
                          {item.tipo_publico || 'Não informado'}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                          <div 
                            className={`h-2 rounded-full ${
                              index === 0 ? 'bg-blue-500' : 
                              index === 1 ? 'bg-green-500' : 
                              index === 2 ? 'bg-yellow-500' : 'bg-gray-500'
                            }`}
                            style={{width: `${getProgressPercentage(item.count, dashboardData.stats.total_complaints)}%`}}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{item.count}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    Nenhum dado de público disponível
                  </div>
                )}
              </div>
            </div>

            <div className="rf-surface p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo Rápido</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm text-gray-600">Últimos 7 dias</span>
                  <span className="text-lg font-semibold text-blue-600">{dashboardData.stats.last_7_days}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-gray-600">Últimos 30 dias</span>
                  <span className="text-lg font-semibold text-green-600">{dashboardData.stats.last_30_days}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                  <span className="text-sm text-gray-600">Taxa de Resolução</span>
                  <span className="text-lg font-semibold text-yellow-600">
                    {getProgressPercentage(dashboardData.stats.resolved_complaints, dashboardData.stats.total_complaints)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'complaints' && (
        <div className="rf-surface p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Lista de Denúncias</h3>
            <div className="flex space-x-2">
              <select className="border border-gray-300 rounded px-3 py-1 text-sm">
                <option value="">Todos os status</option>
                <option value="received">Recebida</option>
                <option value="investigating">Em Investigação</option>
                <option value="resolved">Resolvida</option>
                <option value="closed">Fechada</option>
              </select>
            </div>
          </div>
          
          {/* Filtro por níveis de administração */}
          <AdminLevelFilter
            userLevels={user?.admin_levels || []}
            selectedLevel={selectedLevel}
            onFilterChange={handleFilterChange}
            totalComplaints={filteredComplaints.length}
            formFields={formFields}
          />
          <div className="overflow-x-auto">
            <table className="w-full max-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    ID
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                    Token
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                    Tipo de Denúncia
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Status
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                    Data
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredComplaints && filteredComplaints.length > 0 ? (
                  filteredComplaints.map((complaint) => (
                    <tr key={complaint.id} className="hover:bg-gray-50">
                      {/* Coluna ID numérico */}
                      <td className="px-2 py-4 text-sm font-bold text-blue-900">{complaint.id}</td>
                      {/* Coluna Token com botão de cópia */}
                      <td className="px-2 py-4 text-sm text-gray-900">
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded break-all">{complaint.tracking_token.substring(0, 12)}...</span>
                          <button
                            title="Copiar Token"
                            className="text-blue-500 hover:text-blue-700 text-xs border border-blue-200 rounded px-1 py-0.5"
                            onClick={() => navigator.clipboard.writeText(complaint.tracking_token)}
                          >
                            Copiar
                          </button>
                        </div>
                      </td>
                      {/* Coluna Tipo de Denúncia */}
                      <td className="px-2 py-4 text-sm text-gray-900 break-words">
                        {complaint.tipo_denuncia || (complaint.responses && complaint.responses['tipo_denuncia']) || <span className="text-gray-400 italic">(não informado)</span>}
                      </td>
                      <td className="px-2 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(complaint.status)}`}>
                          {getStatusLabel(complaint.status)}
                        </span>
                      </td>
                      <td className="px-2 py-4 text-sm text-gray-500 break-words">
                        {formatDate(complaint.created_at)}
                      </td>
                      <td className="px-2 py-4 text-sm font-medium">
                        <button className="text-blue-600 hover:text-blue-900 whitespace-nowrap" onClick={() => handleShowDetail(complaint.id)}>
                          Ver
                        </button>
                        {/* Select de status removido para client-admin */}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      {selectedLevel === 'all' 
                        ? 'Nenhuma denúncia encontrada' 
                        : `Nenhuma denúncia encontrada para o nível selecionado`
                      }
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Modal Detalhe com Abas */}
      {showDetail && (
        <div className="rf-modal">
          <div className="rf-modal__panel" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            {/* Header do Modal */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <h2 className="text-lg font-bold text-blue-800">Detalhes da Denúncia</h2>
              <button 
                className="text-gray-500 hover:text-gray-800 text-xl font-bold p-1 hover:bg-gray-200 rounded-full transition-colors" 
                onClick={() => setShowDetail(false)}
              >
                &times;
              </button>
            </div>
            {/* Abas do Modal */}
            <div className="border-b border-gray-200 bg-white">
              <div className="flex">
                <button
                  onClick={() => setModalActiveTab('denuncia')}
                  className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                    modalActiveTab === 'denuncia'
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  📝 Detalhes da Denúncia
                </button>
                <button
                  onClick={() => setModalActiveTab('admin')}
                  className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                    modalActiveTab === 'admin'
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  🛡️ Campos Administrativos
                </button>
              </div>
            </div>
            {/* Conteúdo do Modal (sem overflow-y-auto aqui) */}
            <div className="flex-1 p-4">
              {detailLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-3 text-gray-600 text-sm">Carregando detalhes...</p>
                </div>
              ) : detailError ? (
                <div className="text-center text-red-600 py-6">
                  <p className="text-base font-semibold">Erro ao carregar detalhes</p>
                  <p className="text-sm">{detailError}</p>
                </div>
              ) : detailData ? (
                <div className="space-y-4">
                  {/* Informações Básicas - Sempre visíveis */}
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <h3 className="font-semibold text-blue-800 mb-2 text-sm">📋 Informações Básicas</h3>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="font-medium text-gray-600 text-left">Status:</div>
                      <div className="text-right">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(detailData.complaint.status)}`}>
                          {getStatusLabel(detailData.complaint.status)}
                        </span>
                      </div>
                      <div className="font-medium text-gray-600 text-left">Data:</div>
                      <div className="text-right text-gray-900">{formatDate(detailData.complaint.created_at)}</div>
                      <div className="font-medium text-gray-600 text-left">Token:</div>
                      <div className="text-right font-mono text-xs text-gray-900">{detailData.complaint.tracking_token}</div>
                    </div>
                  </div>
                  {/* Conteúdo da Aba de Denúncia */}
                  {modalActiveTab === 'denuncia' && (
                    <>
                      {/* Só mostra o bloco Relato se NÃO houver respostas do formulário */}
                      {!(detailData.form_fields && detailData.responses) && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <h3 className="font-semibold text-gray-800 mb-3 text-sm flex items-center">
                        📝 Relato (Somente Leitura)
                      </h3>
                      <div className="space-y-2">
                        {Object.entries(camposRelatoLabels).map(([key, label]) => (
                          <div key={key} className="border-b border-gray-100 pb-2 last:border-b-0">
                            <div className="font-medium text-gray-700 text-xs mb-1">{label}</div>
                            <div className="text-gray-900 text-sm">
                              {detailData.relato[key] !== undefined ? 
                                String(detailData.relato[key]) : 
                                <span className="text-gray-400 italic text-xs">(não informado)</span>
                              }
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                      {/* Sempre mostra respostas do formulário se existirem */}
                      {detailData.form_fields && detailData.responses && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3 mt-4">
                      <h3 className="font-semibold text-gray-800 mb-3 text-sm flex items-center">
                        📄 Respostas do Formulário
                      </h3>
                      <div className="space-y-2">
                        {detailData.form_fields.map((field) => {
                          const response = detailData.responses[field.name];
                          if (field.type === 'file') {
                            const attachmentUrl = buildAttachmentUrl(response);
                            // Campo de anexo
                            if (response && attachmentUrl) {
                              return (
                                <div key={field.id} className="border-b border-gray-100 pb-2 last:border-b-0">
                                  <div className="font-medium text-gray-700 text-xs mb-1">{field.label}</div>
                                  <div className="text-gray-900 text-sm">
                                    <a
                                      href={attachmentUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 underline font-semibold"
                                    >
                                      📎 Baixar documento enviado pelo denunciante
                                    </a>
                                  </div>
                                </div>
                              );
                            } else {
                              return (
                                <div key={field.id} className="border-b border-gray-100 pb-2 last:border-b-0">
                                  <div className="font-medium text-gray-700 text-xs mb-1">{field.label}</div>
                                  <div className="text-gray-400 italic text-xs">(não respondido)</div>
                                </div>
                              );
                            }
                          }
                          // Campos normais
                          return (
                            <div key={field.id} className="border-b border-gray-100 pb-2 last:border-b-0">
                              <div className="font-medium text-gray-700 text-xs mb-1">{field.label}</div>
                              <div className="text-gray-900 text-sm">
                                {response !== undefined && response !== null && response !== '' ? String(response) : <span className="text-gray-400 italic text-xs">(não respondido)</span>}
                              </div>
                            </div>
                          );
                        })}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Conteúdo da Aba Administrativa */}
                  {modalActiveTab === 'admin' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <h3 className="font-semibold text-gray-800 mb-3 text-sm flex items-center">
                        🛡️ Campos Administrativos (Somente Leitura)
                      </h3>
                      <div className="space-y-2">
                        {(() => {
                          const adminData = detailData.administrativos || {};
                          // Mapeamentos de aliases para compatibilidade com diferentes chaves do backend
                          const aliasMap = {
                            // texto curto
                            status_conclusao: ['status_conclusao'],
                            data_conclusao: ['data_conclusao'],
                            // respostas ao denunciante
                            resposta_inicial_denunciante: ['resposta_inicial'],
                            resposta_denunciante: ['resposta_final'],
                            // planejamento e conclusão
                            plano_investigacao: ['plano_investigacao'],
                            fatos_apurados: ['fatos_apurados'],
                            conclusao: ['conclusao'],
                            // ações e extras (aceitar ambos nomes para retrocompatibilidade)
                            plano_acao: ['plano_acao_realizado', 'plano_acao'],
                            // não listado nos labels mas pode existir
                            resumo_denuncia: ['resumo_denuncia'],
                            informacoes_adicionais: ['informacoes_adicionais']
                          };

                          const getValue = (labelKey) => {
                            const candidates = aliasMap[labelKey] || [labelKey];
                            for (const k of candidates) {
                              const v = adminData[k];
                              if (v !== undefined && v !== null && String(v).trim() !== '') return v;
                            }
                            return null;
                          };

                          return Object.entries(camposAdminLabels).map(([key, label]) => {
                            const value = getValue(key);
                            return (
                              <div key={key} className="border-b border-gray-200 pb-2 last:border-b-0">
                                <div className="font-medium text-gray-700 text-xs mb-1">{label}</div>
                                <div className="text-gray-900 text-sm">
                                  {value !== null ? String(value) : (
                                    <span className="text-gray-400 italic text-xs">(não informado)</span>
                                  )}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            
            {/* Footer do Modal */}
            <div className="p-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button 
                onClick={() => setShowDetail(false)}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientAdminDashboard; 
